import { getDataApiUrl, API_CONFIG } from '../config/api';
import authService from './authService';
import { AircraftPartRequest } from '../types/aircraft';
import { AircraftPartResponse } from '../types/aircraft';

export interface Flight {
  id: number;
  flightNumber: string;
  departureAirport: string;
  arrivalAirport: string;
  departureTime: string;
  arrivalTime: string;
}

export interface Car {
  id: number;
  name: string;
  model: string;
  number: number;
}

export interface Status {
  id: number;
  name: string;
  description: string;
  color: string;
}

export interface User {
  id: number;
  email: string;
  username: string;
  name: string;
  surname: string;
  patronymic: string;
}

export interface Request {
  id: number;
  title: string;
  description: string;
  flight: Flight;
  car: Car;
  status: Status;
  creator: User;
  responsible: User;
  aircraftParts: AircraftPartResponse[]; // Изменено на массив частей
  priority: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RequestsResponse {
  content: Request[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    offset: number;
    paged: boolean;
    unpaged: boolean;
  };
  last: boolean;
  totalPages: number;
  totalElements: number;
  size: number;
  number: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  first: boolean;
  numberOfElements: number;
  empty: boolean;
}

export interface UserRequestStats {
  userId: number;
  totalRequests: number;
  createdRequests: number;
  responsibleRequests: number;
}

export interface CreateRequestRequest {
  title: string;
  description?: string;
  flightId?: number;
  carId?: number;
  statusId: number;
  creatorId: number;
  responsibleId?: number;
  aircraftParts?: AircraftPartRequest[];
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  liquidType?: 'TYPE_1' | 'TYPE_2' | 'TYPE_3';
}

export interface UpdateRequestRequest {
  title?: string;
  description?: string;
  flightId?: number;
  carId?: number;
  statusId?: number;
  responsibleId?: number;
  aircraftParts?: AircraftPartRequest[];
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  liquidType?: 'TYPE_1' | 'TYPE_2' | 'TYPE_3';
}

export interface SearchRequestRequest {
  title?: string;
  flightId?: number;
  carId?: number;
  statusId?: number;
  creatorId?: number;
  responsibleId?: number;
  aircraftPartType?: string;
  liquidType?: 'TYPE_1' | 'TYPE_2' | 'TYPE_3';
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  isActive?: boolean;
  createdAtFrom?: string;
  createdAtTo?: string;
}

class RequestsService {
  private getAuthHeaders(): HeadersInit {
    const token = authService.getAccessToken();
    console.log('getAuthHeaders - token:', token);
    
    const headers = {
      ...API_CONFIG.HEADERS,
      'Authorization': `Bearer ${token}`
    };
    
    console.log('getAuthHeaders - final headers:', headers);
    return headers;
  }

  async getRequests(page: number = 0, size: number = 20): Promise<RequestsResponse> {
    try {
      const url = getDataApiUrl(`${API_CONFIG.ENDPOINTS.REQUESTS}?page=${page}&size=${size}`);
      const headers = this.getAuthHeaders();
      
      console.log('Request URL:', url);
      console.log('Request headers:', headers);
      console.log('Access token:', authService.getAccessToken());
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response body:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching requests:', error);
      throw error;
    }
  }

  async createRequest(requestData: Partial<Request>): Promise<Request> {
    try {
      const url = getDataApiUrl(API_CONFIG.ENDPOINTS.REQUESTS);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating request:', error);
      throw error;
    }
  }

  async updateRequest(id: number, requestData: Partial<Request>): Promise<Request> {
    try {
      const url = getDataApiUrl(`${API_CONFIG.ENDPOINTS.REQUESTS}/${id}`);
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating request:', error);
      throw error;
    }
  }

  async deleteRequest(id: number): Promise<void> {
    try {
      const url = getDataApiUrl(`${API_CONFIG.ENDPOINTS.REQUESTS}/${id}`);
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error deleting request:', error);
      throw error;
    }
  }

  // Получение заявок по ID ответственного
  async getRequestsByResponsible(userId: number): Promise<Request[]> {
    try {
      const url = getDataApiUrl(`${API_CONFIG.ENDPOINTS.REQUESTS_BY_RESPONSIBLE}/${userId}`);
      const headers = this.getAuthHeaders();
      
      console.log('getRequestsByResponsible - URL:', url);
      console.log('getRequestsByResponsible - Headers:', headers);
      console.log('getRequestsByResponsible - User ID:', userId);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
      });

      console.log('getRequestsByResponsible - Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('getRequestsByResponsible - Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('getRequestsByResponsible - Success response:', data);
      return data;
    } catch (error) {
      console.error('Error fetching requests by responsible:', error);
      throw error;
    }
  }

  // Получение всех заявок пользователя (как создателя, так и ответственного)
  async getRequestsByUser(userId: number): Promise<Request[]> {
    try {
      const url = getDataApiUrl(`${API_CONFIG.ENDPOINTS.REQUESTS_BY_USER}/${userId}`);
      const headers = this.getAuthHeaders();
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching requests by user:', error);
      throw error;
    }
  }

  // Получение статистики заявок пользователя
  async getUserRequestStats(userId: number): Promise<UserRequestStats> {
    try {
      const url = getDataApiUrl(`${API_CONFIG.ENDPOINTS.REQUESTS_BY_USER}/${userId}/stats`);
      const headers = this.getAuthHeaders();
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching user request stats:', error);
      throw error;
    }
  }
}

const requestsService = new RequestsService();
export default requestsService; 